import React, { Suspense, memo } from 'react';

// Loading component with better UX
const LoadingSpinner = ({ message = "Loading..." }) => (
  <div className="flex flex-col justify-center items-center min-h-[200px] p-8">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
    <p className="text-gray-600 text-sm">{message}</p>
  </div>
);

// Error boundary for lazy components
class LazyErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Lazy component error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col justify-center items-center min-h-[200px] p-8 text-center">
          <div className="text-red-500 mb-4">
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Something went wrong</h3>
          <p className="text-gray-600 mb-4">Failed to load component</p>
          <button 
            onClick={() => window.location.reload()} 
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Reload Page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

// Performance-optimized lazy wrapper
const LazyWrapper = memo(({ 
  children, 
  fallback, 
  errorBoundary = true,
  loadingMessage 
}) => {
  const FallbackComponent = fallback || <LoadingSpinner message={loadingMessage} />;
  
  if (errorBoundary) {
    return (
      <LazyErrorBoundary>
        <Suspense fallback={FallbackComponent}>
          {children}
        </Suspense>
      </LazyErrorBoundary>
    );
  }

  return (
    <Suspense fallback={FallbackComponent}>
      {children}
    </Suspense>
  );
});

LazyWrapper.displayName = 'LazyWrapper';

export { LazyWrapper, LoadingSpinner, LazyErrorBoundary };
